import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { Models } from '../api/api-types';
import { DenyRule } from '../api/models-slice.generated';

const normalizeDate = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const m = moment(value as moment.MomentInput);
    return m.isValid() ? m.format('YYYY-MM-DDTHH:mm:ss') : null;
};
const normalizeString = (value: unknown): string | null => {
    if (value === null || value === undefined) {
        return null;
    }
    const trimmed = String(value).trim();
    return trimmed.length === 0 ? null : trimmed;
};

const normalizeNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
};

// Shared DataRobot credential block used by Decision/Price/Risk/BidWinRate/Conversion models.
const normalizeDataRobot = (dataRobot: { project?: string | null; model?: string | null; deployment?: string | null; isLegacy?: boolean } | null | undefined) => {
    if (!dataRobot) {
        return null;
    }
    return {
        isLegacy: Boolean(dataRobot.isLegacy),
        project: normalizeString(dataRobot.project),
        model: normalizeString(dataRobot.model),
        deployment: normalizeString(dataRobot.deployment),
    };
};
const normalizeDenyRules = (
    rules: DenyRule[] | null | undefined
): Record<string, unknown>[] => {
    return (rules ?? []).map((rule) => {
        const ruleData = rule as DenyRule & {
            notnull?: boolean | null;
            notNull?: boolean | null;
        };

        return {
            type: normalizeString(rule.type),
            key: normalizeString(rule.key),
            compareKey: normalizeString(rule.compareKey),
            symbol: normalizeString(rule.symbol)?.toUpperCase(),
            value: normalizeString(rule.value),
            operator: normalizeString(rule.operator),

            // Support both API and form property names.
            notnull: ruleData.notnull ?? ruleData.notNull ?? null,

            denyRules: normalizeDenyRules(rule.denyRules),
        };
    });
};
// Normalizes the model-type-specific payload. Only the block matching `modelType` is persisted.
const normalizeSubModel = (model: Models): Record<string, unknown> | null => {
    switch (model.modelType) {
        case 'Decision':
            return model.decision
                ? {
                      acceptLimit: normalizeNumber(model.decision.acceptLimit),
                      acceptSymbol: model.decision.acceptSymbol?.toUpperCase() ?? null,
                      denyLimit: normalizeNumber(model.decision.denyLimit),
                      denySymbol: model.decision.denySymbol?.toUpperCase() ?? null,
                      dataRobot: normalizeDataRobot(model.decision.dataRobot),
                  }
                : null;
        case 'Price':
            return model.price
                ? {
                      minAmount: normalizeNumber(model.price.minAmount),
                      maxAmount: normalizeNumber(model.price.maxAmount),
                      slope: normalizeNumber(model.price.slope),
                      intercept: normalizeNumber(model.price.intercept),
                      dataRobot: normalizeDataRobot(model.price.dataRobot),
                  }
                : null;
        case 'Risk':
            return model.risk
                ? {
                      increment: normalizeNumber(model.risk.increment),
                      scoreModifier: normalizeNumber(model.risk.scoreModifier),
                      scoreMultiplier: normalizeNumber(model.risk.scoreMultiplier),
                      range: {
                          low: normalizeNumber(model.risk.range?.low),
                          high: normalizeNumber(model.risk.range?.high),
                      },
                      dataRobot: normalizeDataRobot(model.risk.dataRobot),
                  }
                : null;
        case 'BidWinRate':
            return model.bidWinRate
                ? {
                      minAmount: normalizeNumber(model.bidWinRate.minAmount),
                      maxAmount: normalizeNumber(model.bidWinRate.maxAmount),
                      dataRobot: normalizeDataRobot(model.bidWinRate.dataRobot),
                  }
                : null;
        case 'Conversion':
            return model.conversion
                ? {
                      minAmount: normalizeNumber(model.conversion.minAmount),
                      maxAmount: normalizeNumber(model.conversion.maxAmount),
                      minValue: normalizeNumber(model.conversion.minValue),
                      dataRobot: normalizeDataRobot(model.conversion.dataRobot),
                  }
                : null;
        case 'Deny':
            return {
                denyRules: normalizeDenyRules(model.denyRules),
            };
        default:
            return null;
    }
};

export const normalizeModel = (model: Models) => ({
    name: normalizeString(model.name),
    activeStart: normalizeDate(model.activeStart),
    activeEnd: normalizeDate(model.activeEnd),
    groupId: normalizeNumber(model.groupId),
    groupWeight: normalizeNumber(model.groupWeight),
    modelType: model.modelType,
    lowAmount: normalizeNumber(model.lowAmount),
    highAmount: normalizeNumber(model.highAmount),
    preReqs: [...(model.preReqs ?? [])].sort((a, b) => a - b),
    subModel: normalizeSubModel(model),
});

export const hasModelChanges = (originalModel: Models, updatedModel: Models): boolean =>
    !isEqual(normalizeModel(originalModel), normalizeModel(updatedModel));
